'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["playerColor"];
import tinymce from 'tinymce';
import { SpacingIconTypes } from 'rich-text-lib/constants/spacing';
import { getFetchCtaInfo, getFetchWebInteractiveInfo } from 'tinymce-data/api/Cta';
import { getFetchFormInfo, getFetchForms } from 'tinymce-data/api/Form';
import VideoPopoverManager from '../components/VideoPopoverManager';
import { hsvideoConfig, VIDEO_SETTINGS, VIDEO_ACCESS_SCOPE, CTAS_ACCESS_SCOPE, FORMS_ACCESS_SCOPE } from '../constants/hsvideo';
import { OPEN_VIDEO_PICKER, OPEN_EMBED_MODAL } from '../constants/usageTracking';
import { trackInteraction } from '../utils/usageTracking';
import { hasScope } from '../utils/settings';
import { i18nWithNameSpace } from '../utils/i18n';
import { getPluginSetting } from '../utils';
import { getRemoveVideoHandler, getUpdateVideoParams } from './handlers';
import { CANVAS_VIDEO } from '../constants/toolbar';
import { isEditableVideo } from '../utils/video';
import { getVideoContextualToolbar } from '../constants/contextualToolbar';
import { getFetchVideoAttributes, getTrackVideoInteraction, getVideoAttributes } from './util';
import { jsx as _jsx } from "react/jsx-runtime";
const i18nKey = i18nWithNameSpace('TinymcePlugins.hsvideo');
export function addControls(editor, pluginName, openVideoPicker, openEmbedModal, popoverOptions) {
  const {
    [hsvideoConfig.DISABLE_SPACING]: disableMarginControls,
    [hsvideoConfig.DISABLE_ALIGNMENT]: disableAlignmentControls,
    [hsvideoConfig.USE_EMBED]: useEmbed,
    [hsvideoConfig.ENABLE_FULL_WIDTH]: enableFullWidth,
    [hsvideoConfig.USE_CONVERSION_ASSET_OPTIONS_PANEL]: useConversionAssetOptionsPanel
  } = getPluginSetting(editor, VIDEO_SETTINGS, {});
  const videoClickHandler = () => {
    openVideoPicker();
    trackInteraction(editor, 'tinymceVideo', OPEN_VIDEO_PICKER, {
      method: 'combinedInsert',
      type: 'video'
    });
  };
  const menuConfig = {
    text: i18nKey('menuItem')
  };
  if (useEmbed) {
    const embedClickHandler = () => {
      openEmbedModal();
      trackInteraction(editor, 'tinymceVideo', OPEN_EMBED_MODAL, {
        method: 'combinedInsert',
        type: 'embed'
      });
    };
    menuConfig.child = () => [{
      label: i18nKey('hubspotVideo'),
      onClick: videoClickHandler,
      'data-test-id': 'nestedcanvasvideo'
    }, {
      label: i18nKey('embedVideo'),
      onClick: embedClickHandler,
      'data-test-id': 'nestedcanvasembed'
    }];
  } else {
    menuConfig.onClick = videoClickHandler;
  }
  tinymce.canvas.addMenuItem(editor, CANVAS_VIDEO, menuConfig);
  const {
    scopes,
    apiClient
  } = editor.settings;
  const hasVideoAccess = hasScope(VIDEO_ACCESS_SCOPE, scopes);
  const showCtaOverlayOption = hasVideoAccess && hasScope(CTAS_ACCESS_SCOPE, scopes);
  const showFormOverlayOption = hasVideoAccess && hasScope(FORMS_ACCESS_SCOPE, scopes);
  const isActiveNodeHandler = ({
    element
  }) => {
    if (!element) return false;
    return isEditableVideo(editor, element) && element;
  };
  const getActiveNodeAttributes = node => {
    const _getVideoAttributes = getVideoAttributes(editor, node),
      {
        playerColor
      } = _getVideoAttributes,
      videoAttributes = _objectWithoutPropertiesLoose(_getVideoAttributes, _excluded);
    return Object.assign({}, videoAttributes, {
      fillColor: playerColor ? {
        color: playerColor,
        alpha: 100
      } : null,
      fillColorTooltip: i18nKey('playerColorTooltip'),
      dimensionsDisabledTooltip: i18nKey('dimensionsDisabledTooltip'),
      marginIconType: SpacingIconTypes.VIDEO,
      useFullWidth: true
    });
  };
  tinymce.canvas.addNodeChangeHandler(editor, {
    eventKey: pluginName,
    isActiveNodeHandler,
    getActiveNodeAttributes,
    contextualToolbar: getVideoContextualToolbar({
      showAlignmentControls: !disableAlignmentControls,
      showMarginControls: !disableMarginControls,
      showFullWidthControl: enableFullWidth
    })
  });
  const contextualProps = {
    eventKey: pluginName,
    onUpdateVideoParams: getUpdateVideoParams(editor),
    onVideoReplace: openVideoPicker,
    fetchVideoAttributes: getFetchVideoAttributes(editor)
  };
  tinymce.canvas.addPopover(editor, pluginName, {
    renderPopover(renderProps) {
      const handleFetchCtaInfo = guid => {
        return isNaN(Number(guid)) ? getFetchCtaInfo(apiClient)(guid) : getFetchWebInteractiveInfo(apiClient)(guid);
      };
      return /*#__PURE__*/_jsx(VideoPopoverManager, Object.assign({
        editor: editor,
        onVideoRemove: getRemoveVideoHandler(editor),
        fetchCtaInfo: handleFetchCtaInfo,
        fetchFormInfo: getFetchFormInfo(apiClient),
        fetchForms: getFetchForms(apiClient),
        showCtaOverlayOption: showCtaOverlayOption,
        showFormOverlayOption: showFormOverlayOption,
        trackVideoInteraction: getTrackVideoInteraction(editor),
        useConversionAssetOptionsPanel: useConversionAssetOptionsPanel
      }, popoverOptions, renderProps, contextualProps));
    }
  });
}